<template>
  <div class="cert-page">
    <div class="cert-shell">
      <header class="topbar">
        <button class="logo" type="button" @click="$router.replace('/Home')">
          {{ brandName }}
        </button>

        <div class="top-actions">
          <div class="menu" :class="{ open: showLangMenu }">
            <button class="pill" type="button" @click.stop="toggleLangMenu">
              <span class="pill-lab">{{ currentLanguage.label }}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 9l6 6 6-6"></path>
              </svg>
            </button>

            <div v-if="showLangMenu" class="menu-pop lang-pop">
              <button
                v-for="item in languageOptions"
                :key="item.code"
                type="button"
                :class="{ active: displayLang === item.code }"
                @click.stop="changeLang(item)"
              >
                <span>{{ item.short }}</span>
                <b>{{ item.fullLabel }}</b>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main class="page-body">
        <section class="hero-card">
          <div class="hero-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 3l8 4v6c0 5-3.4 7.8-8 9-4.6-1.2-8-4-8-9V7l8-4z"></path>
              <path d="M9 12l2 2 4-4"></path>
            </svg>
          </div>
          <h2>{{ copy.heroTitle }}</h2>
          <p>{{ copy.heroDesc }}</p>
        </section>

        <section class="benefits">
          <div v-for="item in benefitItems" :key="item.title" class="benefit">
            <b>{{ item.title }}</b>
            <span>{{ item.desc }}</span>
          </div>
        </section>

        <section class="section-head">
          <div>
            <h3>{{ authEntryTitle }}</h3>
            <p v-if="sectionSubText">{{ sectionSubText }}</p>
          </div>
          <span class="meta">{{ projectCountText }}</span>
        </section>

        <section class="project-list">
          <button
            v-for="item in displayProjects"
            :key="item.id"
            class="project-card"
            type="button"
            @click="goProject(item)"
          >
            <img class="project-cover" :src="item.image" :alt="item.title" />

            <div class="project-copy">
              <b>{{ item.title }}</b>
              <small>
                <span>{{ item.countdown }}</span>
                <span>·</span>
                <span>{{ authStatusLabel }}</span>
              </small>
              <strong>{{ item.amount }}</strong>
            </div>

            <span class="project-btn">{{ copy.goVerify }}</span>
          </button>

          <div v-if="!displayProjects.length" class="empty-card">
            <b>{{ copy.emptyTitle }}</b>
            <span>{{ copy.emptyDesc }}</span>
          </div>
        </section>
      </main>
    </div>

    <van-popup v-model="serviceVisible" class="service-popup" round>
      <div class="service-card">
        <button class="service-close" type="button" @click="serviceVisible = false">×</button>
        <div class="service-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M21.7 3.2 18.5 20c-.2 1.2-.9 1.5-1.9.9l-4.8-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.9 8.9-8c.4-.3-.1-.5-.6-.2L6.1 13.9 1.4 12.4c-1-.3-1-1 .2-1.5L20 3.8c.9-.3 1.8.2 1.7-.6Z"
            ></path>
          </svg>
        </div>
        <h3>{{ kefuTitle }}</h3>
        <p class="service-sub">{{ copy.serviceDesc }}</p>

        <button
          v-for="agent in serviceAgents"
          :key="agent.name"
          class="service-item"
          type="button"
          @click="openAgent(agent.link)"
        >
          <span class="service-item-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M21.7 3.2 18.5 20c-.2 1.2-.9 1.5-1.9.9l-4.8-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.9 8.9-8c.4-.3-.1-.5-.6-.2L6.1 13.9 1.4 12.4c-1-.3-1-1 .2-1.5L20 3.8c.9-.3 1.8.2 1.7-.6Z"
              ></path>
            </svg>
          </span>
          <span class="service-item-copy">
            <b>{{ agent.name }}</b>
            <small>{{ copy.serviceOnline }}</small>
          </span>
          <em>{{ kefuActionText }}</em>
          <span class="service-arrow">›</span>
        </button>

        <button class="later-btn" type="button" @click="serviceVisible = false">
          {{ copy.serviceLater }}
        </button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { getPageCopy } from "@/common/pageLocale";
import { resolveAssetUrl } from "@/common/runtimeConfig";
import {
  DEFAULT_LANGUAGE_OPTION,
  LANGUAGE_OPTIONS,
  applyAppLanguage,
  findLanguageOption,
  readSavedLanguageOption,
} from "@/common/languages";
import { getKefuContact, openKefuContact, resolveLanguageOptions } from "@/common/kefu";

export default {
  data() {
    return {
      displayLang: "zh_cn",
      showLangMenu: false,
      serviceVisible: false,
      languageOptions: LANGUAGE_OPTIONS,
      projectList: [],
      projectCountdowns: {},
      clockTimer: null,
      countdownRefreshing: false,
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "game");
    },
    baseInfo() {
      return this.$store.getters.getBaseInfo || {};
    },
    ui() {
      return this.baseInfo.ui || {};
    },
    brandName() {
      return String(this.ui.brand || this.baseInfo.name || "OMN")
        .replace(/MShub/gi, "OMN")
        .trim() || "OMN";
    },
    authEntryTitle() {
      const raw = this.ui.auth_entry_title || this.copy.sectionTitle || "认证入口";
      return String(raw)
        .replace(/MShub/gi, "OMN")
        .replace(/认证项目/g, "认证入口")
        .replace(/激活入口/g, "认证入口")
        .replace(/资质认证\s*/g, "")
        .trim() || "认证入口";
    },
    authStatusLabel() {
      const raw = this.ui.auth_status_label || this.copy.reviewText || "系统审核";
      return String(raw).replace(/官方审核/g, "系统审核").trim() || "系统审核";
    },
    sectionSubText() {
      return String(this.copy.sectionSub || "").trim();
    },
    kefuContact() {
      return getKefuContact(this.baseInfo);
    },
    kefuTitle() {
      return this.kefuContact.title || this.copy.serviceTitle;
    },
    kefuActionText() {
      return this.kefuContact.action_text || this.copy.serviceAction;
    },
    benefitItems() {
      return this.copy.benefits;
    },
    currentLanguage() {
      return findLanguageOption(this.displayLang) || this.languageOptions[0] || DEFAULT_LANGUAGE_OPTION;
    },
    serviceFallback() {
      return Array.isArray(this.copy.serviceFallback) ? this.copy.serviceFallback : [];
    },
    serviceAgents() {
      const contact = this.kefuContact;
      if (contact.url) {
        return [{ name: contact.name || this.copy.serviceExclusiveName, link: contact.url }, ...this.serviceFallback];
      }
      return this.serviceFallback;
    },
    displayProjects() {
      return this.projectList.map((item, index) => {
        const amountValue = Number(item.condition || 0) > 0 ? Number(item.condition).toFixed(2) : "280.00";
        const countdownKey = item.key || item.id || `game-${index + 1}`;
        return {
          id: item.id || index + 1,
          key: countdownKey,
          title: this.resolveProjectTitle(item),
          desc: this.resolveProjectDesc(item),
          image: this.resolveProjectImage(item),
          countdown: this.formatProjectTime(this.projectCountdowns[countdownKey]),
          amount: `${this.copy.amountPrefix} ${amountValue} ${this.copy.amountSuffix}`,
        };
      });
    },
    projectCountText() {
      if (this.displayLang === "en_us") {
        return `${this.displayProjects.length} ${this.copy.sectionCount}`;
      }
      return `${this.displayProjects.length} ${this.copy.sectionCount}`;
    },
  },
  watch: {
    baseInfo: {
      deep: true,
      handler(info) {
        this.languageOptions = resolveLanguageOptions(info, LANGUAGE_OPTIONS);
      },
    },
  },
  created() {
    this.languageOptions = resolveLanguageOptions(this.baseInfo, LANGUAGE_OPTIONS);
    this.initLanguage();
    this.fetchProjects();
  },
  mounted() {
    window.addEventListener("click", this.handleWindowClick);
    this.startClock();
  },
  beforeDestroy() {
    window.removeEventListener("click", this.handleWindowClick);
    this.stopClock();
  },
  methods: {
    initLanguage() {
      this.applyLanguage(readSavedLanguageOption(), false);
    },
    applyLanguage(language, closeMenu = true) {
      const next = applyAppLanguage(this, language);
      this.displayLang = next.code;
      if (closeMenu) {
        this.showLangMenu = false;
      }
    },
    changeLang(language) {
      this.applyLanguage(language);
    },
    toggleLangMenu() {
      this.showLangMenu = !this.showLangMenu;
    },
    handleWindowClick() {
      this.showLangMenu = false;
    },
    startClock() {
      this.stopClock();
      this.clockTimer = setInterval(() => {
        const nextCountdowns = {};
        let needsRefresh = false;
        Object.keys(this.projectCountdowns).forEach((key) => {
          const current = Number(this.projectCountdowns[key] || 0);
          const nextValue = Math.max(0, current - 1);
          nextCountdowns[key] = nextValue;
          if (current > 0 && nextValue === 0) {
            needsRefresh = true;
          }
        });
        this.projectCountdowns = nextCountdowns;
        if (needsRefresh) {
          this.fetchProjectCountdowns();
        }
      }, 1000);
    },
    stopClock() {
      if (this.clockTimer) {
        clearInterval(this.clockTimer);
        this.clockTimer = null;
      }
    },
    async fetchProjects() {
      try {
        const res = await this.$http({
          method: "get",
          url: "lottery_list",
        });
        this.projectList = Array.isArray(res && res.data) ? res.data : [];
        await this.fetchProjectCountdowns();
      } catch (error) {
        this.projectList = [];
        this.projectCountdowns = {};
      }
    },
    async fetchProjectCountdowns() {
      if (!this.projectList.length || this.countdownRefreshing) {
        return;
      }
      this.countdownRefreshing = true;
      try {
        const countdownEntries = await Promise.all(
          this.projectList.map(async (item, index) => {
            const countdownKey = item.key || item.id || `game-${index + 1}`;
            if (!item.key) {
              return [countdownKey, 0];
            }
            try {
              const res = await this.$http({
                method: "get",
                data: { key: item.key },
                url: "lottery_get_info",
              });
              if (res && res.code === 200 && res.data && !Array.isArray(res.data)) {
                return [countdownKey, Math.max(0, Number(res.data.second || 0))];
              }
            } catch (error) {
              return [countdownKey, 0];
            }
            return [countdownKey, 0];
          })
        );
        this.projectCountdowns = countdownEntries.reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});
      } finally {
        this.countdownRefreshing = false;
      }
    },
    resolveProjectTitle(item) {
      const currentLang = this.currentLanguage && this.currentLanguage.code;
      const fieldMap = {
        zh_cn: "name_zh_cn",
        zh_tw: "name_zh_cn",
        zh_hk: "name_zh_cn",
        en_us: "name_en_us",
      };
      const preferredField = fieldMap[currentLang];
      let title = "";

      if (preferredField && item[preferredField]) {
        title = item[preferredField];
      } else if (item.name) {
        title = item.name;
      } else if (item.name_zh_cn) {
        title = item.name_zh_cn;
      } else if (item.name_en_us) {
        title = item.name_en_us;
      } else {
        title = this.copy.projectName;
      }

      return String(title || "")
        .replace(/MShub/gi, "OMN")
        .replace(/认证项目/g, "认证入口")
        .replace(/激活入口/g, "认证入口")
        .replace(/资质认证\s*/g, "")
        .trim() || this.copy.projectName;
    },
    resolveProjectDesc(item) {
      const currentLang = this.currentLanguage && this.currentLanguage.code;
      let desc = this.copy.projectDesc;
      if (
        (currentLang === "zh_cn" || currentLang === "zh_tw" || currentLang === "zh_hk") &&
        item.rule &&
        String(item.rule).trim() &&
        Number(item.rule) > 10
      ) {
        desc = item.rule;
      }
      return String(desc || "")
        .replace(/认证项目/g, "认证入口")
        .replace(/激活入口/g, "认证入口")
        .trim();
    },
    resolveProjectImage(item) {
      if (item.ico) {
        return resolveAssetUrl(item.ico);
      }
      return "/img/null.png";
    },
    formatProjectTime(seconds) {
      const total = Math.max(0, Number(seconds) || 0);
      if (!total) {
        return "00:00:00";
      }
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const remainSeconds = total % 60;
      return [hours, minutes, remainSeconds].map((value) => String(value).padStart(2, "0")).join(":");
    },
    goProject(item) {
      if (!localStorage.getItem("token")) {
        this.$router.push("/Login");
        return;
      }
      this.$router.push({ path: "/Lottery", query: { key: item.key, id: item.id } });
    },
    openAgent(link) {
      openKefuContact(this, { url: link });
    },
  },
};
</script>

<style lang="less" scoped>
.cert-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 50% 0, rgba(165, 92, 255, 0.18), transparent 28%),
    linear-gradient(180deg, #140818 0%, #06040c 100%);
  color: #fff;
}

.cert-shell {
  width: 100%;
  max-width: 23.4375rem;
  margin: 0 auto;
  padding: 0 0.75rem 6.75rem;
}

.topbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: max(0.75rem, env(safe-area-inset-top)) 0 0.5rem;
}

.logo {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  border: 0;
  padding: 0;
  background: transparent;
  color: #fff;
  font-size: 1.9rem;
  line-height: 1;
  font-weight: 950;
  letter-spacing: -0.08rem;
  white-space: nowrap;
}

.logo em {
  margin-left: 0.2rem;
  padding: 0.2rem 0.45rem 0.28rem;
  border-radius: 0.65rem;
  color: #190b12;
  font-style: normal;
  background: linear-gradient(135deg, #ff739f, #ff3f78);
  box-shadow: 0 0.45rem 1.35rem rgba(255, 63, 120, 0.28);
}

.top-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.menu {
  position: relative;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 2.35rem;
  max-width: 100%;
  padding: 0 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1.2rem;
  background: rgba(255, 255, 255, 0.055);
  color: #fff7fb;
  font-size: 0.82rem;
  font-weight: 800;
  box-shadow: 0 0.5rem 1.4rem rgba(0, 0, 0, 0.18);
}

.pill-lab {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pill svg {
  width: 0.9rem;
  height: 0.9rem;
  flex: 0 0 auto;
}

.menu-pop {
  position: absolute;
  top: calc(100% + 0.45rem);
  right: 0;
  min-width: 8.8rem;
  padding: 0.35rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(24, 16, 31, 0.98);
  box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.4);
  z-index: 30;
}

.menu-pop button {
  width: 100%;
  padding: 0.7rem 0.8rem;
  border: 0;
  border-radius: 0.8rem;
  background: transparent;
  color: #fff7fb;
  font-size: 0.82rem;
  font-weight: 700;
  text-align: left;
}

.lang-pop button {
  display: grid;
  grid-template-columns: 2rem 1fr;
  align-items: center;
  gap: 0.5rem;
}

.lang-pop button.active {
  background: rgba(255, 63, 120, 0.14);
  color: #ff91b1;
}

.lang-pop {
  min-width: 11rem;
}

.lang-pop button span {
  width: 2rem;
  height: 2rem;
  border-radius: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: #ff9bb8;
  font-size: 0.62rem;
  font-weight: 950;
}

.lang-pop button b {
  font-size: 0.76rem;
}

.page-body {
  padding-top: 1rem;
}

.hero-card {
  padding: 1.2rem 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1.45rem;
  background: linear-gradient(180deg, rgba(63, 22, 72, 0.92), rgba(27, 14, 37, 0.96));
  box-shadow: 0 1rem 2.4rem rgba(0, 0, 0, 0.28);
}

.hero-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 1rem;
  background: linear-gradient(145deg, rgba(168, 104, 255, 0.32), rgba(128, 66, 214, 0.26));
  color: #c084fc;
}

.hero-icon svg {
  width: 1.45rem;
  height: 1.45rem;
}

.hero-card h2 {
  margin: 1rem 0 0;
  font-size: 1.84rem;
  line-height: 1.08;
}

.hero-card p {
  margin: 0.7rem 0 0;
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.82rem;
  line-height: 1.5;
}

.benefits {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
  margin-top: 0.75rem;
}

.benefit {
  padding: 0.9rem 0.5rem 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1rem;
  background: rgba(14, 9, 20, 0.96);
  text-align: center;
}

.benefit b {
  display: block;
  font-size: 0.84rem;
}

.benefit span {
  display: block;
  margin-top: 0.28rem;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.68rem;
}

.section-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
}

.section-head h3 {
  margin: 0;
  font-size: 1.8rem;
  line-height: 1.05;
}

.section-head p {
  margin: 0.45rem 0 0;
  color: rgba(255, 255, 255, 0.66);
  font-size: 0.8rem;
}

.meta {
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.79rem;
  font-weight: 700;
  white-space: nowrap;
}

.project-list {
  margin-top: 0.7rem;
}

.project-card {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1.4rem;
  background: linear-gradient(180deg, rgba(15, 10, 21, 0.98), rgba(10, 7, 16, 0.98));
  box-shadow: 0 1rem 2.2rem rgba(0, 0, 0, 0.24);
  color: #fff;
  text-align: left;
}

.project-cover {
  flex-shrink: 0;
  width: 4rem;
  height: 4rem;
  border-radius: 1rem;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.06);
}

.project-copy {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.project-copy b {
  font-size: 0.94rem;
  line-height: 1.2;
}

.project-copy span {
  margin-top: 0.28rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.72rem;
  line-height: 1.45;
}

.project-copy small {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.45rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.68rem;
}

.project-copy strong {
  margin-top: 0.45rem;
  color: #f1bfd1;
  font-size: 0.76rem;
  font-weight: 700;
}

.project-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 5.25rem;
  height: 2.3rem;
  padding: 0 0.9rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff6aa2, #ff4180);
  box-shadow: 0 0.7rem 1.5rem rgba(255, 80, 136, 0.28);
  color: #fff;
  font-size: 0.76rem;
  font-weight: 800;
}

.empty-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.2rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1.25rem;
  background: rgba(14, 9, 20, 0.92);
}

.empty-card b {
  font-size: 0.94rem;
}

.empty-card span {
  color: rgba(255, 255, 255, 0.66);
  font-size: 0.78rem;
}

.service-popup {
  width: min(20rem, calc(100% - 2rem));
  border-radius: 1.5rem;
  overflow: hidden;
  background: transparent;
}

.service-card {
  position: relative;
  padding: 1.45rem 1rem 1rem;
  background: linear-gradient(180deg, rgba(32, 17, 45, 0.98), rgba(15, 10, 24, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.service-close {
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
}

.service-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  margin: 0 auto;
  border-radius: 1rem;
  background: linear-gradient(180deg, #48b6ff, #1696e8);
  color: #fff;
}

.service-icon svg {
  width: 1.65rem;
  height: 1.65rem;
}

.service-card h3 {
  margin: 0.9rem 0 0;
  text-align: center;
  font-size: 1.42rem;
}

.service-sub {
  margin: 0.6rem 0 1rem;
  color: rgba(255, 255, 255, 0.72);
  text-align: center;
  font-size: 0.74rem;
  line-height: 1.6;
}

.service-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-top: 0.6rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 88, 146, 0.3);
  border-radius: 1rem;
  background: rgba(23, 14, 34, 0.9);
  color: #fff;
  text-align: left;
}

.service-item-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.55rem;
  height: 2.55rem;
  border-radius: 0.85rem;
  background: linear-gradient(180deg, #48b6ff, #1696e8);
}

.service-item-icon svg {
  width: 1.35rem;
  height: 1.35rem;
}

.service-item-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.service-item-copy b {
  font-size: 0.88rem;
}

.service-item-copy small {
  color: #6bf4b4;
  font-size: 0.66rem;
}

.service-item em {
  color: #ff6e9b;
  font-size: 0.74rem;
  font-style: normal;
  font-weight: 700;
}

.service-arrow {
  color: rgba(255, 255, 255, 0.72);
  font-size: 1.1rem;
}

.later-btn {
  width: 100%;
  height: 2.85rem;
  margin-top: 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.95rem;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.84rem;
  font-weight: 700;
}

button {
  cursor: pointer;
}

@media (max-width: 47.9375rem) {
  .cert-shell {
    max-width: 24.25rem;
    padding-left: 0.625rem;
    padding-right: 0.625rem;
  }
}

@media (max-width: 22.5rem) {
  .cert-shell {
    max-width: 23.5rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .logo {
    font-size: 1.72rem;
  }

  .benefits {
    gap: 0.45rem;
  }

  .benefit {
    padding-left: 0.35rem;
    padding-right: 0.35rem;
  }

  .section-head h3,
  .hero-card h2 {
    font-size: 1.62rem;
  }
}

@media (min-width: 48rem) {
  .cert-shell {
    max-width: 25rem;
  }
}
</style>
